import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  MessageSquare, ShieldCheck, ShieldAlert, AlertTriangle, Loader2,
  RefreshCw, CheckCircle2, XCircle, Phone, Clock, ExternalLink,
} from 'lucide-react';
import api from '../utils/api';

/**
 * Iter-200 — SMS Pipeline Health (Admin Ops).
 *
 * Pulls `GET /api/sms/health` and renders a single card that answers:
 *   • Is Twilio auth healthy?
 *   • Is the Twilio account active?
 *   • Is our sender number provisioned?
 *   • Is A2P 10DLC blocking carrier delivery? (Error 30034 / 30007 / 30035)
 *   • What was the last outbound message, and what was its status?
 *
 * Mounted on /admin/dashboard Overview tab alongside DataIntegrityCard
 * and SwVersionDistributionCard.
 */

const StatusRow = ({ ok, label, value, testid }) => (
  <div
    className="flex items-center justify-between py-2 px-3 rounded-md bg-gray-50 border border-gray-100"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-sm text-gray-700">
      {ok === true && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
      {ok === false && <XCircle className="w-4 h-4 text-rose-600" />}
      {ok === null && <AlertTriangle className="w-4 h-4 text-amber-500" />}
      <span>{label}</span>
    </div>
    <span className="text-xs font-mono text-gray-900 max-w-[60%] truncate" title={String(value ?? '')}>
      {value === null || value === undefined || value === '' ? '—' : String(value)}
    </span>
  </div>
);

const formatTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
};

const SmsPipelineHealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/sms/health');
      setData(res?.data || null);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load SMS health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = () => { setRefreshing(true); load(); };

  if (loading) {
    return (
      <Card data-testid="sms-health-card-loading">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-gray-500" />
            SMS Pipeline Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (err || !data) {
    return (
      <Card data-testid="sms-health-card-error" className="border-rose-200">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2 text-rose-700">
            <ShieldAlert className="w-4 h-4" />
            SMS Pipeline Health — unavailable
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-gray-600">
          {err || 'No data returned from /api/sms/health'}
          <div className="mt-2">
            <Button size="sm" variant="outline" onClick={refresh} data-testid="sms-health-retry-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const healthy = !!data.healthy;
  const deliveryBlocked = !!data.delivery_blocked;
  const a2p = data.a2p_carrier_block || null;
  const a2pCount24h = Number(data.a2p_blocked_24h || 0);
  const delivered24h = Number(data.delivered_24h || 0);
  const failed24h = Number(data.failed_24h || 0);
  const recent = data.recent_24h || {};
  const last = data.last_message || null;
  const authOk = !!data.twilio_auth_ok;
  const acctStatus = data.twilio_account_status || null;
  const sender = data.sender_phone || data.messaging_service_sid || null;
  const senderProvisioned = data.sender_number_provisioned;

  // Top-level tone — A2P block overrides healthy bool visually
  const tone = deliveryBlocked
    ? 'border-amber-300'
    : healthy
      ? 'border-emerald-200'
      : 'border-rose-300';

  return (
    <Card data-testid="sms-health-card" className={tone}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-sm flex items-center gap-2 text-gray-900">
            <MessageSquare className="w-4 h-4 text-indigo-600" />
            SMS Pipeline Health
            {healthy && (
              <Badge
                className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] uppercase"
                data-testid="sms-health-status-healthy"
              >
                <ShieldCheck className="w-3 h-3 mr-1" /> Healthy
              </Badge>
            )}
            {!healthy && deliveryBlocked && (
              <Badge
                className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] uppercase"
                data-testid="sms-health-status-carrier-blocked"
              >
                <AlertTriangle className="w-3 h-3 mr-1" /> Carrier blocked
              </Badge>
            )}
            {!healthy && !deliveryBlocked && (
              <Badge
                className="bg-rose-50 text-rose-700 border border-rose-200 text-[10px] uppercase"
                data-testid="sms-health-status-degraded"
              >
                <ShieldAlert className="w-3 h-3 mr-1" /> Degraded
              </Badge>
            )}
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={refresh}
            disabled={refreshing}
            data-testid="sms-health-refresh-btn"
          >
            {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            <span className="ml-1">Refresh</span>
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* A2P 10DLC carrier-block warning banner */}
        {deliveryBlocked && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 p-3"
            data-testid="sms-a2p-warning-banner"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-900 space-y-1.5">
                <div className="font-semibold">
                  SMS is connected — but carriers are blocking delivery (A2P 10DLC registration incomplete).
                </div>
                <div className="text-xs text-amber-800">
                  Twilio is accepting messages, but US carriers (AT&amp;T / T-Mobile / Verizon) are
                  rejecting them with <span className="font-mono font-semibold">Error 30034</span>{' '}
                  because the sending brand/campaign is not registered with The Campaign Registry
                  (A2P 10DLC). Until registration is approved, NO talent or client will receive SMS
                  notifications. Invites are still being attempted but will silently fail.
                </div>
                <div className="text-xs text-amber-800">
                  <span className="font-semibold">{a2pCount24h}</span> message(s) returned 30034 in the last 24h
                  · <span className="font-semibold">{delivered24h}</span> delivered
                  · <span className="font-semibold">{failed24h}</span> failed/undelivered.
                  {a2p?.at && <> Most recent carrier block: {formatTime(a2p.at)}.</>}
                </div>
                <a
                  href="https://www.twilio.com/docs/messaging/compliance/a2p-10dlc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 underline mt-1"
                  data-testid="sms-a2p-docs-link"
                >
                  A2P 10DLC registration guide <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Pipeline rows */}
        <div className="space-y-1.5">
          <StatusRow
            ok={data.sms_enabled === true}
            label="Twilio configured"
            value={data.sms_enabled ? (data.sandbox_mode ? 'Sandbox' : 'Live') : 'Disabled'}
            testid="sms-row-enabled"
          />
          <StatusRow
            ok={authOk}
            label="Twilio auth"
            value={authOk
              ? `OK · ${data.account_sid_prefix || ''}`
              : (data.twilio_error || 'Failed')}
            testid="sms-row-auth"
          />
          <StatusRow
            ok={acctStatus === 'active'}
            label="Account status"
            value={acctStatus || 'Unknown'}
            testid="sms-row-account-status"
          />
          <StatusRow
            ok={senderProvisioned === true || senderProvisioned === null}
            label="Sender number"
            value={sender || 'Not configured'}
            testid="sms-row-sender"
          />
          <StatusRow
            ok={!deliveryBlocked}
            label="A2P 10DLC delivery"
            value={
              deliveryBlocked
                ? `Blocked · ${a2pCount24h} in 24h · code ${a2p?.error_code || '30034'}`
                : 'No carrier blocks detected'
            }
            testid="sms-row-a2p"
          />
        </div>

        {/* Last message detail */}
        <div className="rounded-lg border border-gray-200 bg-white p-3" data-testid="sms-last-message">
          <div className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-gray-500" />
            Last outbound message
          </div>
          {last ? (
            <div className="space-y-1 text-xs text-gray-700">
              <div className="flex justify-between gap-2">
                <span className="text-gray-500">Status</span>
                <span
                  className={
                    last.status === 'delivered' ? 'text-emerald-700 font-medium'
                      : last.status === 'failed' || last.status === 'undelivered' ? 'text-rose-700 font-medium'
                        : 'text-gray-900 font-medium'
                  }
                  data-testid="sms-last-status"
                >
                  {last.status || 'unknown'}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-gray-500">Message SID</span>
                <span className="font-mono text-[11px] text-gray-900 truncate max-w-[60%]" data-testid="sms-last-sid">
                  {last.message_sid || '—'}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-gray-500">Recipient</span>
                <span className="font-mono text-[11px] text-gray-900 inline-flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {last.phone || '—'}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-gray-500">At</span>
                <span className="text-gray-900">{formatTime(last.at)}</span>
              </div>
              {last.error_code && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Error</span>
                  <span
                    className="text-rose-700 font-mono text-[11px] truncate max-w-[60%]"
                    title={last.error_message || ''}
                    data-testid="sms-last-error"
                  >
                    {last.error_code}
                    {last.error_message ? ` · ${last.error_message}` : ''}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500">No outbound SMS recorded yet.</div>
          )}
        </div>

        {/* Last 24h counters */}
        {Object.keys(recent).length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="sms-recent-24h-chips">
            {Object.entries(recent).map(([status, count]) => (
              <Badge
                key={status}
                className={
                  status === 'delivered' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : status === 'failed' || status === 'undelivered' ? 'bg-rose-50 text-rose-700 border border-rose-200'
                      : status === 'skipped' ? 'bg-gray-100 text-gray-700 border border-gray-200'
                        : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                }
              >
                {status}: {count}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SmsPipelineHealthCard;
